import { useState, useEffect } from 'react';
import { useSocket } from '../hooks/useSocket';
import { useCategories } from '../hooks/useCategories';
import { useBranch } from '../contexts/BranchContext';
import Toast from '../components/Toast';

export default function KitchenDisplay({ apiUrl }) {
  const { effectiveBranchId, isAllBranches } = useBranch();
  // จอครัวต้องแสดงออเดอร์ของสาขาเดียวเสมอ — ถ้าเลือก "รวมทุกสาขา" อยู่ effectiveBranchId
  // จะ fallback ไปสาขาแรกตามลำดับตัวอักษรแทน ทำให้ครัวเห็นออเดอร์ผิดสาขาโดยไม่รู้ตัว
  const blocked = isAllBranches || !effectiveBranchId;
  const [orders, setOrders] = useState([]);
  const { categories } = useCategories(apiUrl, effectiveBranchId);
  const [printModal, setPrintModal] = useState({ show: false, order: null });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filterTable, setFilterTable] = useState(null);
  // แจ้ง error ที่เห็นได้จริงบนจอ — เดิมทุก fetch พลาดแล้วแค่ console.error เงียบๆ
  // ทำให้การ์ดหาย/ไม่มีออเดอร์ขึ้นจอโดยไม่มีใครรู้ตัวว่าจริงๆ แล้วมันพลาด
  const [message, setMessage] = useState(null);

  // WebSocket real-time updates
  const { connected, reconnecting } = useSocket(apiUrl, {
    // Listen for new orders
    'order:created': (newOrder) => {
      console.log('New order received:', newOrder);
      if (['submitted', 'preparing'].includes(newOrder.status)) {
        setOrders(prev => [...prev, newOrder].sort((a, b) =>
          new Date(b.createdAt) - new Date(a.createdAt)
        ));
      }
    },

    // Listen for order updates
    'order:updated': (updatedOrder) => {
      console.log('Order updated:', updatedOrder);
      if (['submitted', 'preparing'].includes(updatedOrder.status)) {
        setOrders(prev => {
          const exists = prev.find(o => (o.id || o._id) === (updatedOrder.id || updatedOrder._id));
          if (exists) {
            return prev.map(o =>
              (o.id || o._id) === (updatedOrder.id || updatedOrder._id) ? updatedOrder : o
            );
          }
          return [...prev, updatedOrder].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
          );
        });
      } else {
        // Remove from kitchen display if not active
        setOrders(prev => prev.filter(o =>
          (o.id || o._id) !== (updatedOrder.id || updatedOrder._id)
        ));
      }
    },

    // Listen for order deletions
    'order:deleted': (orderId) => {
      console.log('Order deleted:', orderId);
      setOrders(prev => prev.filter(o => (o.id || o._id) !== orderId));
    }
  }, {
    role: 'kitchen',
    token: localStorage.getItem('adminToken'),
    branchId: effectiveBranchId,
    fallbackEnabled: false // Kitchen display should show real-time only
  });

  // Initial load only (no polling)
  useEffect(() => {
    if (blocked) return;
    loadActiveOrders();
  }, [apiUrl, blocked]);

  const loadActiveOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/orders`, {
        headers: { 'Authorization': `Bearer ${token}`, 'X-Branch-Id': effectiveBranchId }
      });
      if (!response.ok) {
        console.error('Failed to load orders: HTTP', response.status);
        setOrders([]);
        setMessage({ type: 'error', text: 'โหลดออเดอร์ไม่สำเร็จ กรุณารีเฟรชหน้า' });
        return;
      }

      const result = await response.json();

      // รองรับหลายรูปแบบ: {data: []}, {success: true, data: []}, หรือ [] โดยตรง
      let data = Array.isArray(result) ? result : (result.data || []);

      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Invalid data format:', typeof data, data);
        setOrders([]);
        setMessage({ type: 'error', text: 'โหลดออเดอร์ไม่สำเร็จ กรุณารีเฟรชหน้า' });
        return;
      }

      // Filter and sort active orders
      const active = data
        .filter(o => o && ['submitted', 'preparing'].includes(o.status))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setOrders(active);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setOrders([]);
      setMessage({ type: 'error', text: 'โหลดออเดอร์ไม่สำเร็จ กรุณารีเฟรชหน้า' });
    }
  };

  const markAsPreparing = async (orderId) => {
    if (!orderId) {
      console.error('Order ID is undefined');
      return;
    }

    // เก็บ status เดิมไว้ก่อน optimistic update — ถ้า PATCH พลาดต้อง rollback กลับ ไม่ใช่ปล่อย
    // จอค้างที่ค่าใหม่ทั้งที่ DB ยังเป็นค่าเดิม (นี่คือรากของบั๊ก "การ์ดหายไปเงียบๆ")
    const previousStatus = orders.find(o => (o.id || o._id) === orderId)?.status;

    // Update order status in UI immediately (optimistic update)
    setOrders(prevOrders => prevOrders.map(o =>
      (o.id || o._id) === orderId ? { ...o, status: 'preparing' } : o
    ));

    try {
      const token = localStorage.getItem('adminToken');
      const url = `${apiUrl}/api/orders/${orderId}/status`;
      console.log('Updating order to preparing:', { orderId, url });

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Branch-Id': effectiveBranchId
        },
        body: JSON.stringify({ status: 'preparing' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to mark order as preparing:', response.status, errorData);
        setOrders(prevOrders => prevOrders.map(o =>
          (o.id || o._id) === orderId ? { ...o, status: previousStatus ?? o.status } : o
        ));
        setMessage({ type: 'error', text: `เปลี่ยนสถานะไม่สำเร็จ: ${errorData.message || response.status}` });
      } else {
        console.log('Order updated successfully - WebSocket will sync');
      }
    } catch (err) {
      console.error('Failed to update order:', err);
      setOrders(prevOrders => prevOrders.map(o =>
        (o.id || o._id) === orderId ? { ...o, status: previousStatus ?? o.status } : o
      ));
      setMessage({ type: 'error', text: 'เปลี่ยนสถานะไม่สำเร็จ: เชื่อมต่อเซิร์ฟเวอร์ไม่ได้' });
    }
  };

  const markAsServed = async (orderId) => {
    if (!orderId) {
      console.error('Order ID is undefined');
      return;
    }

    // เก็บออเดอร์เดิมไว้ก่อนลบออกจากจอ — ถ้า PATCH พลาดต้องใส่การ์ดกลับคืน ไม่ใช่ปล่อยให้
    // หายไปเงียบๆ ทั้งที่ DB ยังเป็นสถานะเดิม (นี่คือรากของบั๊ก "การ์ดหายไปเงียบๆ")
    const removedOrder = orders.find(o => (o.id || o._id) === orderId);

    // Remove order from UI immediately (optimistic update)
    setOrders(prevOrders => prevOrders.filter(o => (o.id || o._id) !== orderId));

    try {
      const token = localStorage.getItem('adminToken');
      const url = `${apiUrl}/api/orders/${orderId}/status`;
      console.log('Updating order to served:', { orderId, url });

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Branch-Id': effectiveBranchId
        },
        body: JSON.stringify({ status: 'served' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to mark order as served:', response.status, errorData);
        if (removedOrder) {
          setOrders(prevOrders => [...prevOrders, removedOrder].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
          ));
        }
        setMessage({ type: 'error', text: `เปลี่ยนสถานะไม่สำเร็จ: ${errorData.message || response.status}` });
      } else {
        console.log('Order updated successfully - WebSocket will sync');
      }
    } catch (err) {
      console.error('Failed to update order:', err);
      if (removedOrder) {
        setOrders(prevOrders => [...prevOrders, removedOrder].sort((a, b) =>
          new Date(b.createdAt) - new Date(a.createdAt)
        ));
      }
      setMessage({ type: 'error', text: 'เปลี่ยนสถานะไม่สำเร็จ: เชื่อมต่อเซิร์ฟเวอร์ไม่ได้' });
    }
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => (c.id || c._id) === categoryId);
    return category ? category.name : 'อื่นๆ';
  };

  // Group items by category
  const groupItemsByCategory = (items) => {
    const grouped = {};
    items?.forEach(item => {
      const categoryId = item.categoryId || 'uncategorized';
      if (!grouped[categoryId]) {
        grouped[categoryId] = [];
      }
      grouped[categoryId].push(item);
    });
    return grouped;
  };

  // Open print modal
  const openPrintModal = (order) => {
    const grouped = groupItemsByCategory(order.items);
    setSelectedCategories(Object.keys(grouped)); // Select all by default
    setPrintModal({ show: true, order });
  };

  // Close print modal
  const closePrintModal = () => {
    setPrintModal({ show: false, order: null });
    setSelectedCategories([]);
  };

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Select/Deselect all categories
  const toggleAllCategories = (order) => {
    const grouped = groupItemsByCategory(order.items);
    const allCategoryIds = Object.keys(grouped);
    if (selectedCategories.length === allCategoryIds.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(allCategoryIds);
    }
  };

  // Print selected categories - ส่งไปพิมพ์ตามเครื่องพิมพ์ที่ตั้งค่าไว้
  const printSelectedCategories = async (order) => {
    if (selectedCategories.length === 0) {
      setMessage({ type: 'error', text: 'กรุณาเลือกอย่างน้อย 1 หมวดหมู่' });
      return;
    }

    const orderId = order.id || order._id;
    if (!orderId) {
      setMessage({ type: 'error', text: 'ไม่พบ ID ของออเดอร์' });
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/orders/${orderId}/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Branch-Id': effectiveBranchId
        },
        body: JSON.stringify({
          categoryIds: selectedCategories
        })
      });

      const result = await response.json();

      if (response.ok) {
        // แสดงผลลัพธ์การพิมพ์
        const successCount = result.data?.printJobs?.filter(j => j.status === 'queued').length || 0;
        const failedCount = result.data?.printJobs?.filter(j => j.status === 'failed').length || 0;

        if (failedCount > 0) {
          setMessage({ type: 'error', text: `ส่งพิมพ์สำเร็จ ${successCount} หมวดหมู่ ล้มเหลว ${failedCount} หมวดหมู่` });
        } else {
          setMessage({ type: 'success', text: `ส่งพิมพ์สำเร็จ ${successCount} หมวดหมู่` });
        }
        closePrintModal();
      } else {
        throw new Error(result.message || 'ไม่สามารถส่งพิมพ์ได้');
      }
    } catch (err) {
      console.error('Print error:', err);
      setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${err.message}` });
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#fff3cd', color: '#856404', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — จอครัวต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการเห็นออเดอร์ผิดสาขา
        </div>
      </div>
    );
  }

  return (
    <div>
      <Toast message={message} onDismiss={() => setMessage(null)} />
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>จอครัว</h2>
          <p>
            ออเดอร์ที่กำลังดำเนินการ: {orders.length}
            {' '}
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: connected ? '#27ae60' : reconnecting ? '#f39c12' : '#e74c3c',
              marginLeft: '8px'
            }}></span>
            <span style={{ fontSize: '0.9rem', marginLeft: '6px', color: '#7f8c8d' }}>
              {connected ? 'เชื่อมต่อ' : reconnecting ? 'กำลังเชื่อมต่อ...' : 'ไม่ได้เชื่อมต่อ'}
            </span>
          </p>
        </div>
        <button
          onClick={() => {
            setOrders([]);
            loadActiveOrders();
          }}
          className="button"
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          🔄 รีเฟรช
        </button>
      </div>

      {/* Table Filter */}
      {(() => {
        const tableCodes = [...new Set(orders.map(o => o.tableCode).filter(Boolean))].sort((a, b) => {
          const numA = parseInt(a, 10);
          const numB = parseInt(b, 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return String(a).localeCompare(String(b));
        });
        if (tableCodes.length <= 1) return null;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
            <button
              onClick={() => setFilterTable(null)}
              style={{
                padding: '0.5rem 1.2rem',
                borderRadius: '20px',
                border: 'none',
                background: filterTable === null
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : '#2d2d44',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              ทั้งหมด ({orders.length})
            </button>
            {tableCodes.map(code => {
              const count = orders.filter(o => o.tableCode === code).length;
              return (
                <button
                  key={code}
                  onClick={() => setFilterTable(filterTable === code ? null : code)}
                  style={{
                    padding: '0.5rem 1.2rem',
                    borderRadius: '20px',
                    border: 'none',
                    background: filterTable === code
                      ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                      : '#2d2d44',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  โต๊ะ {code} ({count})
                </button>
              );
            })}
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {orders.filter(o => !filterTable || o.tableCode === filterTable).map((order) => (
          <div
            key={order.id || order._id}
            className="card"
            style={{
              borderLeft: `4px solid ${order.status === 'preparing' ? '#f39c12' : '#3498db'}`
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3>โต๊ะ: {order.tableCode || 'ไม่ระบุ'}</h3>
              <span className={`status-badge ${order.status}`}>
                {order.status === 'submitted' ? 'ส่งแล้ว' :
                 order.status === 'preparing' ? 'กำลังเตรียม' : order.status}
              </span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <strong>เวลาสั่ง:</strong>
              <div>{new Date(order.createdAt).toLocaleTimeString('th-TH')}</div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <strong>รายการ:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', listStyle: 'none' }}>
                {order.items?.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{
                        display: 'inline-block',
                        minWidth: '30px',
                        padding: '2px 8px',
                        background: '#f39c12',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }}>
                        ×{item.quantity || 1}
                      </span>
                      <span>{item.name || 'ไม่ทราบชื่อรายการ'}</span>
                    </div>
                    {/* Show item notes (spicy level, etc.) */}
                    {item.notes && (
                      <div style={{
                        marginTop: '0.3rem',
                        marginLeft: '38px',
                        color: '#e74c3c',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem'
                      }}>
                        <span>🌶️</span>
                        <span>{item.notes}</span>
                      </div>
                    )}
                  </li>
                )) || <li>ไม่มีรายการ</li>}
              </ul>
            </div>

            {order.notes && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', background: '#fff3cd', borderRadius: '4px' }}>
                <strong>หมายเหตุ:</strong> {order.notes}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {order.status === 'submitted' && (
                <button
                  className="button"
                  style={{ flex: 1, background: '#f39c12' }}
                  onClick={() => markAsPreparing(order.id || order._id)}
                >
                  เริ่มเตรียมอาหาร
                </button>
              )}
              {order.status === 'preparing' && (
                <button
                  className="button success"
                  style={{ flex: 1 }}
                  onClick={() => markAsServed(order.id || order._id)}
                >
                  เสิร์ฟแล้ว
                </button>
              )}
              <button
                className="button"
                style={{
                  flex: order.status === 'submitted' || order.status === 'preparing' ? 'none' : 1,
                  minWidth: '80px',
                  background: '#3498db'
                }}
                onClick={() => openPrintModal(order)}
              >
                🖨️ พิมพ์
              </button>
            </div>
          </div>
        ))}

        {orders.length === 0 && (
          <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem' }}>
            <p style={{ fontSize: '1.5rem', color: '#95a5a6' }}>ไม่มีออเดอร์ที่กำลังดำเนินการ</p>
          </div>
        )}
      </div>

      {/* Print Category Modal */}
      {printModal.show && printModal.order && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={closePrintModal}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: '16px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem', color: '#fff', textAlign: 'center' }}>
              🖨️ เลือกหมวดหมู่ที่ต้องการพิมพ์
            </h3>
            <p style={{ marginBottom: '1rem', color: '#aaa', textAlign: 'center', fontSize: '0.9rem' }}>
              โต๊ะ: {printModal.order.tableCode}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Select All Checkbox */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 600
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.length === Object.keys(groupItemsByCategory(printModal.order.items)).length}
                  onChange={() => toggleAllCategories(printModal.order)}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span>เลือกทั้งหมด ({printModal.order.items?.length || 0} รายการ)</span>
              </label>

              {/* Category Checkboxes */}
              {Object.entries(groupItemsByCategory(printModal.order.items)).map(([categoryId, items]) => (
                <label
                  key={categoryId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '1rem',
                    background: selectedCategories.includes(categoryId)
                      ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)'
                      : '#2d2d44',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    color: 'white',
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(categoryId)}
                    onChange={() => toggleCategory(categoryId)}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{getCategoryName(categoryId)}</span>
                    <span style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem'
                    }}>
                      {items.length} รายการ
                    </span>
                  </div>
                </label>
              ))}

              {/* Print Button */}
              <button
                onClick={() => printSelectedCategories(printModal.order)}
                disabled={selectedCategories.length === 0}
                style={{
                  padding: '1rem',
                  background: selectedCategories.length > 0
                    ? 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)'
                    : '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  cursor: selectedCategories.length > 0 ? 'pointer' : 'not-allowed',
                  marginTop: '0.5rem'
                }}
              >
                🖨️ พิมพ์ ({selectedCategories.length} หมวดหมู่)
              </button>

              {/* Cancel Button */}
              <button
                onClick={closePrintModal}
                style={{
                  padding: '0.75rem',
                  background: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                ❌ ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
